import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, Clock, Calendar } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { useSeo, organizationSchema, breadcrumbSchema } from "../lib/useSeo";
import { ARTICLES, CATEGORIES, getCategoryArticles } from "../lib/articles";
import { CTASection } from "../components/sections/CTASection";

const fmtDate = (iso, locale) =>
  new Date(iso).toLocaleDateString(locale === "de" ? "de-DE" : "en-GB", {
    year: "numeric", month: "short", day: "numeric",
  });

export default function BlogCategoryPage() {
  const { category } = useParams();
  const { locale } = useI18n();
  const isDE = locale === "de";
  const cat = CATEGORIES.find((c) => c.slug === category);
  const articles = getCategoryArticles(category);

  useSeo({
    title: cat ? `${cat.label[locale]} — Blog | Joshi FusionTech Media` : "Blog | Joshi FusionTech Media",
    description: cat ? cat.tagline[locale] : undefined,
    canonical: `https://www.joshifusiontech.de/blog/${category}`,
    locale,
    jsonLd: cat
      ? [
          organizationSchema(),
          breadcrumbSchema([
            { name: isDE ? "Start" : "Home", url: "https://www.joshifusiontech.de/" },
            { name: "Blog", url: "https://www.joshifusiontech.de/blog" },
            { name: cat.label[locale], url: `https://www.joshifusiontech.de/blog/${category}` },
          ]),
        ]
      : null,
  });

  if (!cat) return <Navigate to="/blog" replace />;

  return (
    <main data-testid={`blog-category-page-${category}`} className="pt-32 pb-24">
      <section className="px-6 md:px-12 lg:px-24 mb-16">
        <div className="max-w-6xl mx-auto">
          <nav aria-label="Breadcrumb" className="mb-6">
            <ol className="flex items-center gap-2 text-xs font-mono text-white/50">
              <li><Link to="/blog" className="hover:text-brand-orange">Blog</Link></li>
              <li className="text-white/30">/</li>
              <li className="text-white/80">{cat.label[locale]}</li>
            </ol>
          </nav>
          <h1 className="font-display font-bold text-4xl md:text-5xl lg:text-6xl tracking-tight text-white leading-[1.05]">
            {cat.label[locale]}
          </h1>
          <p className="mt-5 text-white/65 text-lg max-w-2xl leading-relaxed">{cat.tagline[locale]}</p>
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-24 mb-20">
        <div className="max-w-6xl mx-auto">
          {articles.length === 0 ? (
            <div className="glass rounded-3xl p-10 text-center">
              <div className="text-white/60">{isDE ? "Bald mehr Artikel in dieser Kategorie." : "More articles in this category coming soon."}</div>
              <Link to="/blog" className="btn-secondary mt-6 inline-flex">
                {isDE ? "Alle Artikel ansehen" : "View all articles"} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {articles.map((a) => (
                <Link
                  key={a.slug}
                  to={`/blog/${a.category}/${a.slug}`}
                  data-testid={`blog-card-${a.slug}`}
                  className="glass rounded-2xl overflow-hidden hover:border-brand-orange/40 hover:-translate-y-1 hover:shadow-[0_18px_44px_rgba(255,106,0,0.15)] transition-all duration-300 group flex flex-col"
                >
                  <div className="relative h-44 overflow-hidden">
                    <img
                      src={`/blog/${a.slug}.webp`}
                      alt={a.title[locale]}
                      loading="lazy"
                      decoding="async"
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.06]"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                    <div className="absolute top-3 left-3 px-2.5 py-1 rounded-md bg-brand-orange/90 text-white text-[10px] font-mono uppercase tracking-[0.22em] shadow-lg">
                      {cat.label[locale]}
                    </div>
                  </div>
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="flex items-center gap-3 text-[11px] text-white/45 mb-3">
                      <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />{fmtDate(a.publishedAt, locale)}</span>
                      <span>·</span>
                      <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{a.readMinutes} min</span>
                    </div>
                    <h3 className="font-display font-semibold text-lg text-white tracking-tight leading-[1.2] group-hover:text-brand-orange transition-colors flex-1">
                      {a.title[locale]}
                    </h3>
                    <p className="mt-3 text-sm text-white/60 line-clamp-3">{a.excerpt[locale]}</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <CTASection />
    </main>
  );
}
